import React from 'react';
import { ChangeType, formatValue } from '../utils/diffEngine';

const changeTypeConfig = {
  [ChangeType.ADDED]: {
    label: 'Added',
    emoji: '🟢',
    className: 'change-added'
  },
  [ChangeType.REMOVED]: {
    label: 'Removed',
    emoji: '🔴',
    className: 'change-removed'
  },
  [ChangeType.MODIFIED]: {
    label: 'Modified',
    emoji: '🟡',
    className: 'change-modified'
  },
  [ChangeType.TYPE_CHANGED]: {
    label: 'Type Changed',
    emoji: '🔵',
    className: 'change-type'
  }
};

function DiffEntry({ diff, onPathClick }) {
  const config = changeTypeConfig[diff.changeType];

  return (
    <div className={`diff-entry ${config.className}`}>
      <div className="diff-entry-header">
        <span className="diff-emoji">{config.emoji}</span>
        <button
          className="diff-path"
          onClick={() => onPathClick(diff.path)}
          title="Click to highlight in input"
        >
          {diff.path}
        </button>
        <span className="diff-change-type">{config.label}</span>
      </div>

      <div className="diff-entry-content">
        {diff.changeType === ChangeType.TYPE_CHANGED && (
          <div className="diff-type-change">
            <span className="diff-type-label">Type:</span>
            <span className="diff-old-type">{diff.oldType}</span>
            <span className="diff-arrow">→</span>
            <span className="diff-new-type">{diff.newType}</span>
          </div>
        )}

        {diff.oldValue !== undefined && diff.changeType !== ChangeType.ADDED && (
          <div className="diff-value old-value">
            <span className="diff-value-label">Old:</span>
            <pre className="diff-value-content">{formatValue(diff.oldValue, 200)}</pre>
          </div>
        )}

        {diff.newValue !== undefined && diff.changeType !== ChangeType.REMOVED && (
          <div className="diff-value new-value">
            <span className="diff-value-label">New:</span>
            <pre className="diff-value-content">{formatValue(diff.newValue, 200)}</pre>
          </div>
        )}
      </div>
    </div>
  );
}

export default function SemanticSummary({ diffResult, onPathClick, onCopy }) {
  if (!diffResult || !diffResult.hasChanges) {
    return (
      <div className="semantic-summary empty">
        <div className="empty-state">
          <span className="empty-icon">✓</span>
          <p className="empty-message">No differences found</p>
        </div>
      </div>
    );
  }

  const { diffs, stats } = diffResult;

  return (
    <div className="semantic-summary">
      <div className="summary-stats">
        <div className="stat-item">
          <span className="stat-label">Total Changes:</span>
          <span className="stat-value">{stats.total}</span>
        </div>
        {stats.added > 0 && (
          <div className="stat-item added">
            <span className="stat-emoji">🟢</span>
            <span className="stat-label">Added:</span>
            <span className="stat-value">{stats.added}</span>
          </div>
        )}
        {stats.removed > 0 && (
          <div className="stat-item removed">
            <span className="stat-emoji">🔴</span>
            <span className="stat-label">Removed:</span>
            <span className="stat-value">{stats.removed}</span>
          </div>
        )}
        {stats.modified > 0 && (
          <div className="stat-item modified">
            <span className="stat-emoji">🟡</span>
            <span className="stat-label">Modified:</span>
            <span className="stat-value">{stats.modified}</span>
          </div>
        )}
        {stats.typeChanged > 0 && (
          <div className="stat-item type-changed">
            <span className="stat-emoji">🔵</span>
            <span className="stat-label">Type Changed:</span>
            <span className="stat-value">{stats.typeChanged}</span>
          </div>
        )}
      </div>

      <div className="diff-entries">
        {diffs.map((diff, idx) => (
          <DiffEntry
            key={idx}
            diff={diff}
            onPathClick={onPathClick}
          />
        ))}
      </div>
    </div>
  );
}
